import React from "react";
import styled from "styled-components";
import { COLOR_WHITE, COLOR_BLACK } from "../../common/colors";

// Caption area below the video. Its height is exactly TWO lines tall
// (2 * line-height + vertical padding, with a small buffer)
// NOTE: keep line-height / padding / font-size in sync with the
// height calc below.
const ClosedCaptionsDiv = styled.div`
  height: calc(2 * 1.5em + 16px + 2px);
  box-sizing: border-box;
  overflow: hidden;
  text-align: center;
  max-width: min(500px, calc(65vh * 16 / 9));
  margin: 8px auto 0;
  padding: 8px 16px;
  font-size: 18px;
  line-height: 1.5;
`;

const CaptionText = styled.span`
  background-color: ${COLOR_BLACK};
  color: ${COLOR_WHITE};
  padding: 2px 8px;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`;

// Whitespace the pager may break at. Excludes U+00A0: the backend joins the
// words of a control token ("[♪ Car horn]", "Benny the Bear:") with non-breaking
// spaces precisely so clients keep the token as one unbreakable chunk.
const BREAKABLE_SPACE = /[^\S\u00A0]/;

// Largest word-boundary-aligned slice of `text` starting at `start` that fits
// within the fixed-height caption box. Writes candidate text into the caption
// span `textEl` and measures the box `boxEl` (scrollHeight vs clientHeight) via
// binary search over word boundaries. Always returns at least one word so paging
// can't stall.
const captionFitEnd = (
  boxEl: HTMLElement,
  textEl: HTMLElement,
  text: string,
  start: number
): number => {
  const remainder = text.slice(start);
  if (remainder.length === 0) {
    textEl.textContent = "";
    return start;
  }

  // Offsets (relative to `start`) just after each word: at each breakable
  // whitespace and at the end of the string.
  const breaks: number[] = [];
  for (let i = 1; i <= remainder.length; i++) {
    if (i === remainder.length || BREAKABLE_SPACE.test(remainder[i])) {
      breaks.push(i);
    }
  }

  const fits = (len: number) => {
    textEl.textContent = remainder.slice(0, len).trim();
    return boxEl.scrollHeight <= boxEl.clientHeight;
  };

  if (fits(remainder.length)) {
    return text.length;
  }

  let lo = 0;
  let hi = breaks.length - 1;
  let best = breaks[0]; // at least one word, even if it overflows on its own
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (fits(breaks[mid])) {
      best = breaks[mid];
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return start + best;
};

// Renders the current caption page inside the fixed-height box. `fullText` is
// the whole utterance (caption_full); `progressChars` is how many characters of
// it have been spoken so far (caption_progress prefix length). Paging is derived
// on each update: we walk fixed-height pages from the start of the utterance and
// display the first one that still contains the spoken position, so we advance
// only once speech passes the end of what is shown (chaining across several
// pages if progress jumps). No page state is stored — the page is a pure
// function of (fullText, progressChars, box size).
export const ClosedCaptions: React.FC<{
  fullText: string;
  progressChars: number;
}> = ({ fullText, progressChars }) => {
  const boxRef = React.useRef<HTMLDivElement>(null);
  const textRef = React.useRef<HTMLSpanElement>(null);

  React.useLayoutEffect(() => {
    const boxEl = boxRef.current;
    const textEl = textRef.current;
    if (!boxEl || !textEl) {
      return;
    }
    // captionFitEnd measures the box with candidate text in the span, so the
    // span must be visible during measurement even if the previous page hid it.
    textEl.style.display = "";
    let start = 0;
    let end = captionFitEnd(boxEl, textEl, fullText, start);
    while (end < fullText.length && progressChars >= end) {
      start = end;
      while (start < fullText.length && BREAKABLE_SPACE.test(fullText[start])) {
        start++;
      }
      end = captionFitEnd(boxEl, textEl, fullText, start);
    }
    const pageText = fullText.slice(start, end).trim();
    textEl.textContent = pageText;
    // A page with no visible characters must not paint the background pill.
    textEl.style.display = pageText === "" ? "none" : "";
  }, [fullText, progressChars]);

  return (
    <ClosedCaptionsDiv ref={boxRef}>
      <CaptionText ref={textRef} />
    </ClosedCaptionsDiv>
  );
};
